import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import { requestJson } from "../../../helpers/api";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

type OrdenResumen = {
  id: number;
  recetaId: number;
  cantidad: string | number;
  estado: string;
  sucursalId?: number | null;
  creadoEn: string;
  depositoId?: number | null;
};

type RecetaOption = {
  id: number;
  codigo: string;
  nombre: string;
};

const OrdenesPage = () => {
  const [ordenes, setOrdenes] = useState<OrdenResumen[]>([]);
  const [recetas, setRecetas] = useState<RecetaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    recetaId: "",
    cantidad: "1",
    sucursalId: "",
    depositoId: "",
  });
  const [tabActiva, setTabActiva] = useState<"formularios" | "listado">("formularios");

  const handleApiError = useCallback((error: unknown, fallback: string) => {
    const mensajeError =
      error instanceof Error ? error.message : typeof error === "string" ? error : fallback;
    return mensajeError;
  }, []);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [recetasData, ordenesData] = await Promise.all([
        requestJson<RecetaOption[]>("/api/produccion/recetas"),
        requestJson<OrdenResumen[]>("/api/produccion/ordenes"),
      ]);
      setRecetas(Array.isArray(recetasData) ? recetasData : []);
      setOrdenes(Array.isArray(ordenesData) ? ordenesData : []);
    } catch (err) {
      setError(handleApiError(err, "No se pudieron cargar los datos de producción."));
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setMensaje(null);
    setError(null);
    try {
      const recetaId = Number(form.recetaId);
      const cantidad = Number(form.cantidad);
      if (!Number.isFinite(recetaId) || recetaId <= 0) {
        setError("Seleccioná una receta válida.");
        return;
      }
      if (!Number.isFinite(cantidad) || cantidad <= 0) {
        setError("Ingresá una cantidad válida.");
        return;
      }
      const depositoId = Number(form.depositoId);
      if (!Number.isFinite(depositoId) || depositoId <= 0) {
        setError("Ingresá un depósito válido.");
        return;
      }
      await requestJson("/api/produccion/ordenes", {
        method: "POST",
        body: JSON.stringify({
          recetaId,
          cantidad,
          sucursalId: form.sucursalId ? Number(form.sucursalId) : undefined,
          depositoId,
        }),
      });
      setMensaje("Orden creada correctamente.");
      setForm({ recetaId: "", cantidad: "1", sucursalId: "", depositoId: "" });
      await cargarDatos();
    } catch (err) {
      setError(handleApiError(err, "No se pudo crear la orden."));
    } finally {
      setSaving(false);
    }
  };

  const nombreReceta = useMemo(
    () =>
      recetas.reduce<Record<number, string>>((acc, receta) => {
        acc[receta.id] = `${receta.codigo} – ${receta.nombre}`;
        return acc;
      }, {} as Record<number, string>),
    [recetas],
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          title="Órdenes de producción"
          pageTitle="Fábrica"
          pageLink="/produccion/ordenes"
          description="Lanzá órdenes usando recetas multinivel y rastrea su avance."
        />
        <Nav tabs className="nav-tabs nav-tabs-custom mb-3">
          {[
            { id: "formularios", label: "Crear orden" },
            { id: "listado", label: "Listado de órdenes" },
          ].map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={tabActiva === tab.id ? "active" : ""}
                onClick={() => setTabActiva(tab.id as "formularios" | "listado")}
              >
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <TabContent activeTab={tabActiva}>
          <TabPane tabId="formularios">
            <Card className="mb-4">
              <CardHeader>
                <h5 className="mb-0">Órdenes de producción</h5>
                <p className="mb-0 text-muted">Genera órdenes usando las recetas configuradas.</p>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row className="gy-3">
                    <Col md={4}>
                      <Label className="form-label">Receta</Label>
                      <Input
                        type="select"
                        value={form.recetaId}
                        onChange={(event) => setForm((prev) => ({ ...prev, recetaId: event.target.value }))}
                      >
                        <option value="">Elige una receta</option>
                        {recetas.map((receta) => (
                          <option key={receta.id} value={receta.id}>
                            {receta.codigo} - {receta.nombre}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Label className="form-label">Cantidad</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={form.cantidad}
                        onChange={(event) => setForm((prev) => ({ ...prev, cantidad: event.target.value }))}
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="form-label">Sucursal (ID)</Label>
                      <Input
                        type="number"
                        value={form.sucursalId}
                        onChange={(event) => setForm((prev) => ({ ...prev, sucursalId: event.target.value }))}
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="form-label">Depósito (ID)</Label>
                      <Input
                        type="number"
                        required
                        min="1"
                        value={form.depositoId}
                        onChange={(event) => setForm((prev) => ({ ...prev, depositoId: event.target.value }))}
                      />
                    </Col>
                  </Row>
                  {error && <p className="text-danger mt-2">{error}</p>}
                  {mensaje && <p className="text-success mt-2">{mensaje}</p>}
                  <Button type="submit" color="primary" disabled={saving} className="mt-3">
                    {saving ? "Generando…" : "Crear orden"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </TabPane>
          <TabPane tabId="listado">
            <Card>
              <CardHeader>
                <h5 className="mb-0">Listado de órdenes</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <p>Cargando…</p>
                ) : ordenes.length === 0 ? (
                  <p className="text-muted">Aún no hay órdenes registradas.</p>
                ) : (
                  <Table responsive bordered hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Receta</th>
                        <th>Cantidad</th>
                        <th>Estado</th>
                        <th>Sucursal ID</th>
                        <th>Depósito ID</th>
                        <th>Creada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordenes.map((orden) => (
                        <tr key={orden.id}>
                          <td>{orden.id}</td>
                          <td>{nombreReceta[orden.recetaId] ?? orden.recetaId}</td>
                          <td>{orden.cantidad}</td>
                          <td>{orden.estado}</td>
                          <td>{orden.sucursalId ?? "-"}</td>
                          <td>{orden.depositoId ?? "-"}</td>
                          <td>{orden.creadoEn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
};

export default OrdenesPage;
